import React, { useMemo } from 'react'

import { useHistory } from 'react-router-dom'

import { SxProps } from '@mui/material'

import { TableComponents, TableVirtuoso, TableBodyProps } from 'react-virtuoso'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableHead from 'components_new/atoms/Table/TableHead'
import TableRow from 'components_new/atoms/Table/TableRow'
import Text from 'components_new/atoms/Text'

import GenericListItem from 'components_new/molecules/GenericListItem'

import { getHighlightedMatch } from 'components_new/organisms/StandardSearch/utils'

import Empty from './empty'
import Loading from './loading'

import { useEntities } from 'redux/hooks/Entities'
import { addRef } from 'utils/queryHandler'
import { Context } from 'types/GlobalUserEvents'
import { useTranslation } from 'language/translator'

const VirtuosoTableComponents: TableComponents<any> = {
  Table: (props) => (
    <Table
      {...props}
      sx={{
        tableLayout: 'fixed',
        width: '100%',
        maxWidth: '100%',
        'td, th': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }
      }}
    />
  ),
  TableBody: React.forwardRef<
    HTMLTableSectionElement,
    TableBodyProps & { context?: any }
  >(({ ...props }, ref) => <TableBody {...props} ref={ref} />),
  TableHead: ({ ref, ...props }) => <TableHead {...props} ref={ref} />,
  TableRow: React.forwardRef((props, ref) => {
    return (
      <TableRow
        {...props}
        ref={ref}
        hover={true}
        onClick={() => props.item.onClick()}
        sx={{
          cursor: 'pointer'
        }}
      />
    )
  })
}

interface Props {
  sx?: SxProps
  search: string | null
  category: string | null
}

const EntitiesList = React.memo((props: Props) => {
  const { sx, category, search } = props

  const { t, lang } = useTranslation()
  const history = useHistory()

  const [entities, loading] = useEntities()

  const filteredEntities = useMemo(() => {
    return entities
      .filter((entity) => {
        return (
          (!category || entity.category.slug === category) &&
          (!search ||
            entity.title[lang]
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()))
        )
      })
      .map((entity) => ({
        ...entity,
        onClick: () =>
          history.push(addRef(`/entities/${entity.id}`, Context.FROM_ENTITIES))
      }))
  }, [category, entities, search])

  if (loading) return <Loading />

  // success
  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        height: '100%',
        ...sx
      }}
    >
      <Text color="text.disabled" variant="caption">
        {t('global', 'shows')} <b>{filteredEntities.length}</b>{' '}
        {t('global', 'of', true)} {entities.length}{' '}
        {t('entities', 'entities', true)}
      </Text>

      <Box sx={{ flex: 1 }}>
        {filteredEntities.length === 0 ? (
          <Empty />
        ) : (
          <TableVirtuoso
            data={filteredEntities}
            components={VirtuosoTableComponents}
            fixedHeaderContent={() => (
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell padding="none" sx={{ width: '55%' }}>
                  {t('entities', 'entity')}
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  {t('entities', 'properties')}
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  {t('entities', 'slices')}
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  {category ? t('metrics', 'metric') : t('global', 'category')}
                </TableCell>
                <TableCell padding="checkbox">{''}</TableCell>
              </TableRow>
            )}
            itemContent={(_, entity) => {
              return (
                <>
                  <TableCell padding="none" sx={{ py: 0.5 }}>
                    <GenericListItem
                      customTitle={getHighlightedMatch(
                        entity.title[lang],
                        search
                      )}
                      disablePadding={true}
                      title={entity.title[lang]}
                      variant="entity"
                    />
                  </TableCell>
                  <TableCell>{entity.columns.standard.length}</TableCell>
                  <TableCell>{entity.slices.all.length}</TableCell>
                  <TableCell>
                    {category ? (
                      entity.metrics.all.length
                    ) : (
                      <Chip label={entity.category.title[lang]} size="xsmall" />
                    )}
                  </TableCell>
                  <TableCell padding="checkbox">
                    <Icon fontSize="small" name="ChevronRight" />
                  </TableCell>
                </>
              )
            }}
            style={{
              height: '100%',
              width: '100%'
            }}
          />
        )}
      </Box>
    </Box>
  )
})

export default EntitiesList
